/**
 * Complete-task step — web twin of apps/mobile/components/TaskNoteModal.js
 * (completion: note + up to 3 proof photos).
 *
 * Layout matches native: title + char counter, short description, borderless
 * growing note field, proof thumb row, then Cancel + Confirm pinned above
 * the soft keyboard via visualViewport.
 */
import { useState, useCallback, useRef, useEffect } from 'react';
import { cameraOutline } from 'ionicons/icons';
import { updateTaskStatusFromWeb, uploadProofPhotoFromWeb } from '../services/api';
import { normalizeProofPhoto, proofBlobToBase64 } from '../services/proofPhoto';
import { useVisualViewport } from '../hooks/useVisualViewport';
import IonIcon from '../components/IonIcon';

interface Props {
  shortId: string;
  onDone: () => void;
  onCancel: () => void;
}

const MAX_NOTE_LENGTH = 500;
const MAX_PROOF_PHOTOS = 3;
/** Native TaskNoteModal input: minHeight 80, maxHeight 220. */
const NOTE_MIN_HEIGHT = 80;
const NOTE_MAX_HEIGHT = 220;
/** theme.thumbnails.size.xxl */
const THUMB_SIZE = 64;

interface ProofItem {
  id: string;
  file: File;
  previewUrl: string;
}

function autosizeTextarea(el: HTMLTextAreaElement | null) {
  if (!el) return;
  el.style.height = 'auto';
  const next = Math.min(Math.max(el.scrollHeight, NOTE_MIN_HEIGHT), NOTE_MAX_HEIGHT);
  el.style.height = `${next}px`;
}

export default function CompleteStep({ shortId, onDone, onCancel }: Props) {
  const [comment, setComment] = useState('');
  const [proofs, setProofs] = useState<ProofItem[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const noteRef = useRef<HTMLTextAreaElement>(null);
  const {
    height: vvHeight,
    offsetTop: vvOffsetTop,
    keyboardOpen,
  } = useVisualViewport();

  useEffect(() => {
    return () => {
      proofs.forEach((p) => URL.revokeObjectURL(p.previewUrl));
    };
    // Only revoke on unmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    autosizeTextarea(noteRef.current);
  }, [comment]);

  const removeProof = useCallback((id: string) => {
    setProofs((prev) => {
      const next = prev.filter((p) => p.id !== id);
      const removed = prev.find((p) => p.id === id);
      if (removed) URL.revokeObjectURL(removed.previewUrl);
      return next;
    });
  }, []);

  const handleProofSelected = useCallback(async (file: File | null) => {
    if (!file) return;
    setPreviewLoading(true);
    setError(null);
    try {
      const { blob } = await normalizeProofPhoto(file);
      const previewUrl = URL.createObjectURL(blob);
      const normalizedFile = new File([blob], file.name.replace(/\.\w+$/, '.jpg') || 'proof.jpg', {
        type: 'image/jpeg',
      });
      setProofs((prev) => {
        if (prev.length >= MAX_PROOF_PHOTOS) {
          URL.revokeObjectURL(previewUrl);
          return prev;
        }
        return [
          ...prev,
          {
            id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
            file: normalizedFile,
            previewUrl,
          },
        ];
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Proof preview failed:', err);
      setError(
        err instanceof Error ? err.message : 'Could not load this photo. Try another image.',
      );
    } finally {
      setPreviewLoading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }, []);

  const handleSubmit = useCallback(async () => {
    setError(null);
    setSubmitting(true);
    try {
      const proofStoragePaths: string[] = [];

      for (const proof of proofs) {
        const { blob, contentType } = await normalizeProofPhoto(proof.file);
        const base64 = await proofBlobToBase64(blob);
        const uploadRes = await uploadProofPhotoFromWeb({
          shortId,
          contentType,
          base64,
        });
        if (!uploadRes.ok) {
          throw new Error(uploadRes.error || 'Failed to upload proof photo');
        }
        proofStoragePaths.push(uploadRes.storagePath);
      }

      const statusRes = await updateTaskStatusFromWeb({
        shortId,
        newStatus: 'completed',
        comment: comment.trim() || undefined,
        proofStoragePaths: proofStoragePaths.length > 0 ? proofStoragePaths : undefined,
        proofStoragePath: proofStoragePaths[0],
      });
      if (!statusRes.ok) {
        throw new Error(statusRes.error || 'Failed to complete task');
      }
      onDone();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Complete failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to complete task');
      setSubmitting(false);
    }
  }, [shortId, comment, proofs, onDone]);

  const canAddMore = proofs.length < MAX_PROOF_PHOTOS;
  const addLabel =
    proofs.length === 0 ? 'Add proof photo' : 'Add another photo';

  return (
    <div
      className="fixed inset-x-0 z-50 mx-auto flex w-full max-w-md flex-col bg-white text-x9-ink"
      style={{
        height: vvHeight || '100dvh',
        top: vvOffsetTop,
      }}
    >
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 pt-[max(env(safe-area-inset-top),20px)]">
        <div className="mb-1 flex items-baseline justify-between gap-2">
          <h1 className="min-w-0 flex-1 truncate text-lg font-bold text-x9-ink">
            Complete Task
          </h1>
          <span className="min-w-[3.5rem] shrink-0 text-right text-xs tabular-nums text-x9-marineSoft">
            {comment.length}/{MAX_NOTE_LENGTH}
          </span>
        </div>
        <p className="mb-3 text-sm leading-[18px] text-x9-marineSoft">
          Optionally add a note or proof of completion.
        </p>
        <div className="mb-4 border-t border-x9-marine/10" />

        <textarea
          ref={noteRef}
          id="note"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Completion notes…"
          maxLength={MAX_NOTE_LENGTH}
          autoFocus
          rows={3}
          className="mb-5 w-full resize-none border-0 bg-transparent p-0 text-base text-x9-ink outline-none placeholder:text-x9-marineSoft/70 focus:ring-0"
          style={{ minHeight: NOTE_MIN_HEIGHT, maxHeight: NOTE_MAX_HEIGHT }}
        />

        <div className="mb-4 flex flex-wrap gap-2">
          {proofs.map((proof) => (
            <div
              key={proof.id}
              className="relative"
              style={{ width: THUMB_SIZE, height: THUMB_SIZE }}
            >
              <img
                src={proof.previewUrl}
                alt="Proof preview"
                className="size-full rounded-[5px] object-cover bg-x9-paper"
              />
              <button
                type="button"
                onClick={() => removeProof(proof.id)}
                disabled={submitting}
                aria-label="Remove photo"
                className="absolute -right-1.5 -top-1.5 flex size-5 items-center justify-center rounded-full bg-white text-x9-error"
              >
                <span className="text-base leading-none" aria-hidden="true">×</span>
              </button>
            </div>
          ))}
          {canAddMore && (
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={previewLoading || submitting}
              aria-label={addLabel}
              className="flex items-center justify-center rounded-[5px] border-2 border-dashed border-x9-mute bg-x9-paper text-x9-marineSoft transition hover:border-x9-marineSoft hover:text-x9-marine disabled:opacity-45"
              style={{ width: THUMB_SIZE, height: THUMB_SIZE }}
            >
              {previewLoading ? (
                <span className="text-xs">…</span>
              ) : (
                <IonIcon icon={cameraOutline} size={28} />
              )}
            </button>
          )}
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0] ?? null;
            void handleProofSelected(file);
          }}
        />

        {error && (
          <div className="mb-3 rounded-[10px] border border-x9-error/30 bg-x9-error/5 p-3 text-sm text-x9-error">
            {error}
          </div>
        )}
      </div>

      <footer
        className="shrink-0 border-t border-x9-marine/10 bg-white px-5 pt-2.5"
        style={{
          // Safe-area only when the keyboard is down. While open, the visual
          // viewport already ends at the keyboard — keeping inset-bottom
          // leaves a dead gap above the keys.
          paddingBottom: keyboardOpen
            ? 10
            : 'max(env(safe-area-inset-bottom), 10px)',
        }}
      >
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="min-w-[60px] px-3 py-2 text-center text-base font-medium text-x9-marineSoft transition hover:text-x9-marine disabled:opacity-45"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            aria-busy={submitting}
            className="btn-paper-primary min-h-[48px] flex-1 !rounded-[10px]"
          >
            {submitting ? 'Completing…' : 'Complete Task'}
          </button>
        </div>
      </footer>
    </div>
  );
}
